import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, Clock, Award, Shield, Star } from 'lucide-react';
import EnhancedSEO from '../../components/EnhancedSEO';
import CheckoutButton from '../../components/CheckoutButton';
import ROICalculator from '../../components/ROICalculator';
import ComparisonTable from '../../components/ComparisonTable';
import IndustryCompetitorTable from '../../components/IndustryCompetitorTable';
import NonTechnicalReassurance from '../../components/NonTechnicalReassurance';
import KeyFeaturesPlain from '../../components/KeyFeaturesPlain';
import BusinessValueSection from '../../components/BusinessValueSection';
import { testimonials } from '../../data/mock';

const PlumberOffer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [spotsLeft] = useState(7);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);

  useEffect(() => {
    const calculateTimeToMidnight = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0); // Next midnight
      
      const diff = midnight - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { hours, minutes, seconds };
    };

    // Update immediately
    setTimeLeft(calculateTimeToMidnight());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeToMidnight());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Floating CTA scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Show floating CTA after scrolling 800px (past hero section)
      if (window.scrollY > 800 && !ctaDismissed) {
        setShowFloatingCTA(true);
      } else if (window.scrollY <= 800) {
        setShowFloatingCTA(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ctaDismissed]);

  return (
    <>
      <EnhancedSEO 
        title="Plumber CRM & Marketing Automation | £5 Trial | Local Business Heroes"
        description="Get more plumbing jobs with automated lead capture, instant follow-up, and 24/7 booking. Join 500+ plumbers who doubled their bookings. £5 trial, then £497/month. Cancel anytime."
        keywords="plumber CRM, plumbing software UK, plumber marketing automation, plumbing business software, plumber lead generation, plumber booking system, emergency plumber software"
        url="/offers/plumber-lifetime-upgrade"
        trade="plumber"
        includeFAQ={true}
        includeRating={true}
        testimonials={testimonials.filter(t => t.business === "" || t.text.toLowerCase().includes('plumb'))}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Offers", url: "/pricing" },
          { name: "Plumber Offer", url: "/offers/plumber-lifetime-upgrade" }
        ]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 text-center font-bold sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-4 flex items-center justify-center space-x-4">
            <Clock className="animate-pulse" size={20} />
            <span>OFFER EXPIRES IN: {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Only {spotsLeft} spots left</span>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12 max-w-5xl">
          
          <div className="text-center mb-12">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              ⚠️ EMERGENCY OFFER - PLUMBERS ONLY
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Stop Losing <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">£2,824</span> Per Month to<br />
              Missed Calls & Lost Customers
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              <strong className="text-white">FREE £6,000/Year Upgrade</strong><br />
              Get ALL Hero Domination Features at Hero Growth Price... Forever
            </p>

            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-2 border-orange-500 rounded-2xl p-6 mb-8">
              <p className="text-white text-lg mb-2">
                <strong className="text-2xl text-orange-400">7 Plumbing Companies</strong> Will Get This Today
              </p>
              <p className="text-gray-300">
                You're reading this because you're one of them. But you must act in the next 24 hours or lose this forever.
              </p>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-2xl p-8 mb-12 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Let Me Guess What's Happening Right Now...
            </h2>
            
            <div className="space-y-4">
              {[
                "You're losing 3-4 valuable calls every day because you can't answer whilst you're under a sink",
                "Mrs. Jones called at 9 PM about a burst pipe. She called your competitor by 9:03 PM",
                "You've got 47 unread messages from last week that you still haven't followed up on",
                "That £850 boiler job? They went with someone else because you forgot to send the quote",
                "Your team member forgot to show up AGAIN because nobody confirmed the appointment",
                "You're working 60-hour weeks but your calendar is still a mess of scattered bookings",
                "You missed your daughter's school play because of a 'last-minute' job that could've waited",
                "Your son asked 'Why do you always work?' and you didn't have a good answer",
                "Date night? What's that? You haven't had quality time with your partner in months",
                "Your kids are growing up fast. You're missing it whilst answering calls at the dinner table"
              ].map((problem, i) => (
                <div key={i} className="flex items-start space-x-3 bg-red-900/20 p-4 rounded-lg border border-red-500/30">
                  <X className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">{problem}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-xl text-orange-400 font-bold mb-2">
                Sound familiar? You're not alone.
              </p>
              <p className="text-gray-300 text-lg">
                But here's what you might not realize: <strong className="text-white">Every missed call costs you £385 on average.</strong><br />
                That's £2,824/month. £33,888/year. Gone. Forever.
              </p>
            </div>
          </div>

          {/* Key Features in Plain Language */}
          <div className="mb-12">
            <KeyFeaturesPlain />
          </div>

          {/* ROI Calculator */}
          <div className="mb-12">
            <ROICalculator industry="plumber" />
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <Award className="mx-auto text-orange-400 mb-4" size={48} />
              <h2 className="text-4xl font-black text-white mb-4">
                What If You Never Missed Another Call?
              </h2>
              <p className="text-xl text-gray-200">
                Imagine waking up tomorrow and discovering...
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Every Call Answered - Even at 2 AM",
                  desc: "AI optionally answers when you can't, takes details, books them in. Or send automatic preset replies. Mrs. Jones? Handled within 90 seconds."
                },
                {
                  title: "Zero No-Shows",
                  desc: "Automatic reminders via text the day before. Customers confirm or reschedule automatically."
                },
                {
                  title: "Quotes Sent Instantly",
                  desc: "Customer requests a quote? Automatically in their inbox in 60 seconds. Professionally branded. Ready to book."
                },
                {
                  title: "Follow-Ups That Actually Happen",
                  desc: "Every lead gets 7 automatic follow-ups over 14 days. No more 'I forgot to call them back.'"
                },
                {
                  title: "Reviews Flood In Automatically",
                  desc: "Finish a job. System automatically texts them 2 hours later asking for a review. 67% say yes."
                },
                {
                  title: "Your Calendar Runs Itself",
                  desc: "Customers book themselves online. System optimizes routes. You just show up and work."
                }
              ].map((benefit, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-6 border border-orange-500/30">
                  <CheckCircle2 className="text-green-400 mb-3" size={32} />
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-300">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Real Plumbers, Real Results
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  name: "Mike T.",
                  location: "Manchester",
                  result: "+£47,000 in 90 days",
                  quote: "I was skeptical. But in my first week, I booked 11 jobs while I was actually working. The AI answered everything. I'm working less and earning more."
                },
                {
                  name: "Sarah C.",
                  location: "London",
                  result: "Went from 1 van to 4 vans",
                  quote: "The automated follow-ups are insane. We're converting 73% of quotes now. Before this, we were at 34%. This system paid for itself in 9 days."
                },
                {
                  name: "James R.",
                  location: "Leeds",
                  result: "87 5-star reviews in 6 months",
                  quote: "The review automation is like having a marketing team. We went from 9 reviews to 87 reviews. Google loves us now."
                },
                {
                  name: "Emma W.",
                  location: "Bristol",
                  result: "£2,400 average job value",
                  quote: "The quote follow-up system is magic. Customers who didn't respond are now booking. My average job value went up 40%."
                },
                {
                  name: "Peter K.",
                  location: "Glasgow",
                  result: "Booked 3 months ahead",
                  quote: "I used to scramble for work. Now customers book online while I'm working. My calendar is full 3 months out. Life changing."
                },
                {
                  name: "David M.",
                  location: "Birmingham",
                  result: "£8,200/month recurring revenue",
                  quote: "The membership feature is brilliant. I now have 47 customers on monthly maintenance plans. That's guaranteed income every month whilst I'm focusing on bigger jobs."
                }
              ].map((testimonial, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-200 mb-4 italic">"{testimonial.quote}"</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.location}</p>
                    <p className="text-orange-400 font-bold mt-2">{testimonial.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison Table - First */}
          <div className="mb-12">
            <ComparisonTable industry="plumber" />
          </div>

          {/* Industry Competitor Comparison - After Quick Math */}
          <div className="mb-12">
            <IndustryCompetitorTable industry="plumber" />
          </div>

          {/* Non-Technical Reassurance - New Section */}
          <div className="mb-12">
            <NonTechnicalReassurance />
          </div>

          {/* Business Value Section - Exit Strategy */}
          <div className="mb-12">
            <BusinessValueSection />
          </div>

          <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-4 border-green-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-black text-white mb-4">
                Here's The Deal (Worth £6,000/Year)
              </h2>
              <p className="text-xl text-gray-200">
                For the next {timeLeft.hours} hours only...
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white/5 rounded-xl p-6 relative">
                <div className="absolute -top-3 -right-3 bg-red-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  WHAT YOU PAY
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Hero Growth</h3>
                <p className="text-5xl font-black text-white mb-4">£497<span className="text-xl text-gray-400">/mo</span></p>
                <ul className="space-y-3">
                  {[
                    "Up to 25 users",
                    "10,000 contacts",
                    "Multi-channel campaigns",
                    "Social media scheduler",
                    "ROI dashboard",
                    "Phone + email support"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300">
                      <CheckCircle2 className="text-gray-500 mr-2 flex-shrink-0" size={20} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-xl p-6 border-4 border-orange-500 relative">
                <div className="absolute -top-3 -right-3 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold animate-pulse">
                  WHAT YOU GET
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Hero Domination Features</h3>
                <p className="text-5xl font-black text-white mb-4">£497<span className="text-xl text-gray-400">/mo</span></p>
                <p className="text-orange-400 font-bold mb-4">Usually £997/mo - Save £500/mo = £6,000/year</p>
                <ul className="space-y-3">
                  {[
                    "AI optionally answers when you can't - or send preset replies",
                    "Up to 50 users included",
                    "£50/month in credits (SMS, AI calls, emails) - more than enough for most",
                    "Instant quotes sent automatically",
                    "Never miss an emergency call",
                    "7-stage automatic follow-up",
                    "Automated review requests",
                    "Smart calendar booking",
                    "Missed call text-back (60 seconds)",
                    "Setup done FOR you",
                    "Dedicated success manager",
                    "Built specifically for plumbers",
                    "Priority support"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-white font-medium">
                      <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={20} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center">
              <p className="text-2xl text-white mb-6">
                You Pay Growth Price. You Get Domination Features. <strong className="text-orange-400">For Life.</strong>
              </p>
              
              <div className="space-y-3">
                {/* Monthly Option */}
                <CheckoutButton 
                  plan="PLUMBER_LIFETIME_UPGRADE"
                  industry="plumber"
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white text-lg sm:text-xl md:text-2xl px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all w-full"
                >
                  START £5 TRIAL - Pay Monthly (£497/mo)
                </CheckoutButton>
                
                {/* Annual Option - Save 2 months */}
                <CheckoutButton 
                  plan="PLUMBER_LIFETIME_UPGRADE_ANNUAL"
                  industry="plumber"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-lg sm:text-xl md:text-2xl px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all w-full"
                >
                  START £5 TRIAL - Pay Annually (£4,970/yr - Save £994!)
                </CheckoutButton>
              </div>
              
              <p className="text-gray-300 text-xs sm:text-sm mt-4">
                14-day trial for £5, then choose monthly or annual billing with Hero Domination features forever
              </p>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-2xl p-8 mb-12 border border-white/10">
            <div className="text-center mb-8">
              <Shield className="mx-auto text-green-400 mb-4" size={48} />
              <h2 className="text-3xl font-bold text-white mb-4">
                Our Ironclad 14-Day Money-Back Guarantee
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                We're so confident this will transform your business, we're taking ALL the risk.
              </p>
            </div>

            <div className="bg-green-900/20 border-2 border-green-500 rounded-xl p-6 mb-6">
              <h3 className="text-2xl font-bold text-white mb-4">Here's How It Works:</h3>
              <div className="space-y-4 text-gray-200">
                <p className="text-lg">
                  <strong className="text-green-400">1.</strong> Start your 14-day trial for just £5 today
                </p>
                <p className="text-lg">
                  <strong className="text-green-400">2.</strong> Use the system. Answer calls with AI. Send quotes automatically. Watch leads convert.
                </p>
                <p className="text-lg">
                  <strong className="text-green-400">3.</strong> If for ANY reason you're not completely satisfied within 14 days, simply email us at support@localbusinessheroes.net
                </p>
                <p className="text-lg">
                  <strong className="text-green-400">4.</strong> We'll refund your £5 trial fee + cancel your subscription. No questions asked. No hoops to jump through.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Full Refund</h3>
                <p className="text-gray-300">100% of your £5 trial fee returned. Every penny.</p>
              </div>

              <div className="text-center">
                <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No Questions</h3>
                <p className="text-gray-300">Don't like it? That's reason enough. We won't argue or hassle you.</p>
              </div>

              <div className="text-center">
                <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Price Locked Forever</h3>
                <p className="text-gray-300">Stay with us? £497/month with Domination features. Forever. Never increases.</p>
              </div>
            </div>

            <div className="bg-blue-900/30 border border-blue-500/50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-3">Important Terms (Please Read):</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <p>
                  • By starting your trial, you agree that if you request a refund within 14 days, you will receive a full refund of your £5 trial fee and your subscription will be cancelled immediately.
                </p>
                <p>
                  • After the 14-day trial period, standard monthly billing begins at £497/month. No refunds available after the trial period ends.
                </p>
                <p>
                  • You may cancel your subscription at any time after the trial period. Cancellation takes effect at the end of your current billing cycle.
                </p>
                <p>
                  • This special lifetime upgrade pricing (£497/month for Domination features) is locked in for as long as you remain a customer. If you cancel and return later, standard pricing applies.
                </p>
                <p>
                  • To request a refund during the trial period, email support@localbusinessheroes.net with your account details. Refunds processed within 3-5 business days.
                </p>
                <p className="text-white font-bold mt-4">
                  • By clicking "CLAIM MY LIFETIME UPGRADE" below, you acknowledge and agree to these terms.
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-lg text-gray-300">
                <strong className="text-white">Why can we offer this?</strong><br />
                Because we know once you see how many calls you're capturing, how much time you're saving, and how much more money you're making...<br />
                <span className="text-green-400 text-xl font-bold">You'll never want to go back to the old way.</span>
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-8 text-center">
            <h2 className="text-4xl font-black text-white mb-4">
              Let's Be Brutally Honest Here
            </h2>
            <p className="text-xl text-white mb-6">
              This offer expires in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s<br />
              Only {spotsLeft} plumbing companies will get this lifetime upgrade today.
            </p>
            
            <div className="bg-white/10 rounded-xl p-6 mb-6 max-w-3xl mx-auto">
              <p className="text-white text-xl font-bold mb-4">
                If you won't invest £5 for a 14-day trial...
              </p>
              <p className="text-white/90 text-lg mb-4">
                If you can't risk £5 to potentially add £3,227/month to your pocket...<br />
                If you won't take 14 days to test something that could transform your business...
              </p>
              <p className="text-orange-300 text-lg font-black leading-tight mb-4 border-l-4 border-orange-400 pl-4">
                Then maybe you should save yourself the long, drawn-out pain<br />of running your business — and down tools now.
              </p>
            </div>

            <div className="bg-green-600/30 rounded-xl p-6 mb-6 max-w-3xl mx-auto border-2 border-green-400">
              <p className="text-2xl text-white font-black mb-3">
                But You're Not That Person.
              </p>
              <p className="text-white text-lg">
                You didn't build a business by backing down from opportunities.<br />
                You didn't get this far by being afraid of a £5 risk.<br />
                You know a no-brainer when you see one.
              </p>
            </div>
            
            <CheckoutButton 
              plan="PLUMBER_LIFETIME_UPGRADE"
              industry="plumber"
              className="bg-white text-red-600 text-lg sm:text-xl md:text-2xl px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mb-4 hover:bg-gray-100 w-full sm:w-auto"
            >
              YES! I'M IN - Start £5 Trial Now
            </CheckoutButton>
            
            <p className="text-white text-lg sm:text-xl font-bold mt-4">
              See you on the inside. 👊
            </p>
            <p className="text-white/80 text-sm mt-2">
              Join 347 plumbing companies already using Local Business Heroes
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 max-w-4xl mx-auto">
            <h2 className="text-3xl font-black text-white text-center mb-2">
              Got Questions? We've Got Answers.
            </h2>
            <p className="text-center text-white/70 mb-10">No BS. Just straight answers.</p>
            
            <div className="space-y-4">
              {/* FAQ 1 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  Is this really built for plumbers or just another generic system?
                </h3>
                <p className="text-white/90">
                  We're a platform built for local service businesses. We have industry-specific solutions for plumbers, electricians, HVAC, and more. 
                  Each setup is tailored to your industry—emergency call handling, job tracking, quote follow-ups, review automation. 
                  This isn't one-size-fits-all. It's built for how plumbers actually work.
                </p>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  I'm not tech-savvy. Will I actually use this?
                </h3>
                <p className="text-white/90">
                  If you can send a text, you can use this. No training needed. Most plumbers are up and running in under 5 minutes. 
                  Plus, UK-based support if you get stuck (but you won't).
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  What if it doesn't work for my business?
                </h3>
                <p className="text-white/90">
                  Then you get your £5 back. Full refund within 14 days. If you cancel, your account closes and access ends. 
                  No questions, no hassle, no hard feelings. We only want customers who are making money with us.
                </p>
              </div>

              {/* FAQ 4 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  How is this different from [other CRM]?
                </h3>
                <p className="text-white/90">
                  Other CRMs are built for offices with HR departments. We're built for you. No 47-step setup. No "implementation consultant." 
                  Just tools that work the way plumbers work. Emergency calls, quotes, reviews, done.
                </p>
              </div>

              {/* FAQ 5 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  Can I cancel anytime?
                </h3>
                <p className="text-white/90">
                  Yes. Click a button, you're done. No phone calls, no "let me transfer you to retention." 
                  Cancel in 10 seconds if it's not for you.
                </p>
              </div>

              {/* FAQ 6 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  Why only £5 for 14 days?
                </h3>
                <p className="text-white/90">
                  Stops tyre-kickers. If you won't risk £5 to test something that could add £3,000/month to your business, 
                  you're not serious. We only want serious plumbers who actually want to grow.
                </p>
              </div>

              {/* FAQ 7 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  What happens after the 14-day trial?
                </h3>
                <p className="text-white/90">
                  You choose. Stay on and you'll be charged the monthly rate (Hero Growth plan). Cancel before 14 days? 
                  You only paid £5. Keep the upgrade. That's it.
                </p>
              </div>

              {/* FAQ 8 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  Do you integrate with my existing tools?
                </h3>
                <p className="text-white/90">
                  Probably. We integrate with most accounting software, calendars, and payment systems. 
                  If we don't integrate with something specific, our UK support team will help you figure it out.
                </p>
              </div>

              {/* FAQ 9 */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-orange-400 mb-2">
                  What about your £297/month package?
                </h3>
                <p className="text-white/90">
                  Yes, we have a Hero Growth plan at £297/month. But it doesn't include this lifetime upgrade to Domination features. 
                  This offer gets you Domination features (normally £997/month) for just £497/month — locked in forever. 
                  This upgrade may not be available later. If you take this offer now, you can easily downgrade or cancel inside your account anytime.
                </p>
              </div>
            </div>

            <div className="text-center mt-10">
              <p className="text-white/60 text-sm">
                Still have questions? <a href="/contact" className="text-orange-400 hover:underline font-bold">Ask us anything</a>
              </p>
            </div>
          </div>

          {/* Final CTA After FAQ */}
          <div className="text-center mt-16 max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl p-10 shadow-2xl">
              <h2 className="text-3xl font-black text-white mb-4">
                Ready to Stop Losing Money?
              </h2>
              <p className="text-white/90 text-lg mb-6">
                £5 for 14 days. Then just £497/month for everything.<br />
                <span className="font-bold">Usually £997/month.</span> This offer won't last.
              </p>
              
              <CheckoutButton 
                plan="PLUMBER_LIFETIME_UPGRADE"
                industry="plumber"
                className="bg-white text-red-600 text-lg sm:text-xl md:text-2xl px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mb-4 hover:bg-gray-100 w-full sm:w-auto"
              >
                Claim Your Lifetime Upgrade Now - £5 Trial
              </CheckoutButton>
              
              <p className="text-white/80 text-xs sm:text-sm mt-4">
                ✓ 14-day money-back guarantee &nbsp; | &nbsp; ✓ Cancel anytime &nbsp; | &nbsp; ✓ Setup in 5 minutes
              </p>
            </div>
          </div>

          <div className="text-center mt-12 text-gray-400 text-sm">
            <p>This is a limited-time offer exclusively for plumbing companies.</p>
          </div>

        </div>
      </div>

      {/* Floating Sticky CTA Bar */}
      {showFloatingCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 shadow-2xl border-t-4 border-orange-400 animate-slide-up">
          <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
              {/* Dismiss button - LEFT SIDE */}
              <button
                onClick={() => setCtaDismissed(true)}
                className="text-white/80 hover:text-white p-1 sm:p-2 transition-colors flex-shrink-0"
                aria-label="Dismiss"
              >
                <X size={20} className="sm:w-6 sm:h-6" />
              </button>

              {/* Center - Message (hide on very small screens, show abbreviated) */}
              <div className="hidden sm:block flex-1 min-w-[150px]">
                <p className="text-white font-black text-base sm:text-lg mb-1">
                  🔥 Limited Time: Lifetime Upgrade
                </p>
                <p className="text-white/90 text-xs sm:text-sm">
                  £497/mo for £997/mo features • Only {spotsLeft} spots left
                </p>
              </div>
              
              {/* Mobile - Compact message */}
              <div className="block sm:hidden flex-1 min-w-[100px]">
                <p className="text-white font-black text-sm">
                  🔥 Lifetime Upgrade
                </p>
                <p className="text-white/90 text-xs">
                  {spotsLeft} spots left
                </p>
              </div>

              {/* Right side - Buttons (responsive margin) */}
              <div className="flex gap-1 sm:gap-2 flex-wrap mr-0 sm:mr-20">
                <CheckoutButton 
                  plan="PLUMBER_LIFETIME_UPGRADE"
                  industry="plumber"
                  className="bg-white text-red-600 px-3 sm:px-6 py-2 sm:py-3 rounded-full font-black shadow-xl hover:bg-gray-100 transition-all text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Monthly - £497/mo</span>
                  <span className="inline sm:hidden">Monthly</span>
                </CheckoutButton>
                
                <CheckoutButton 
                  plan="PLUMBER_LIFETIME_UPGRADE_ANNUAL"
                  industry="plumber"
                  className="bg-green-500 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full font-black shadow-xl hover:bg-green-600 transition-all text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Annual - Save £994!</span>
                  <span className="inline sm:hidden">Annual</span>
                </CheckoutButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlumberOffer;